import { useApolloClient } from '@apollo/react-hooks';
import { Textarea, ToggleSwitch } from '../../libs/gs-component-form';
import RightRail, { Content, Footer, Header, TripleAction } from '../../libs/gs-component-right-rail';
import { FormColumn, FormRow } from '@xo-union/tk-component-form-grid';
import { Subhead } from '@xo-union/tk-ui-typography';
import WeddingDatePreferenceForm from '../../libs/gs-component-wedding-date';
import { Field as FormikField, Formik } from 'formik';
import Honeybadger from 'honeybadger-js';
import React, { useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useHistory, useRouteMatch } from 'react-router-dom';
import * as Yup from 'yup';

import {
  MemberArgs,
  useCreateAnnouncementMutation,
  useUpdateAnnouncementMutation,
  useWeddingWebsiteQuery,
  WeddingWebsiteQuery,
  WeddingDatePreferencesQuery,
  WeddingDatePreferences,
} from '../../generated/graphql';
import { useUpdateBasicInfoMutation } from '../../generated/graphql';
import { basicInfoRailRoute, dashboardRoute, previewRoute } from '../../helpers/routes';
import { getTargetOrigin } from '../../helpers/getTargetOrigin';
import { getChangedValues } from '../../helpers/getChangedValues';
import CVAlert from '../CVAlert';
import { weddingWebsiteQuery } from '../Dashboard';
import NameFormSection from './NameFormSection';
import styles from './style.scss';
import { weddingDatePreferencesQuery } from './graphql';
import WeddingLocationField from '../MessagingHub/form/WeddingLocationField';

interface InitialValues {
  firstName: string;
  lastName: string;
  fianceFirstName: string;
  fianceLastName: string;
  weddingLocation: string;
  announcementMessage: string;
  announcementShow: boolean;
}

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('First Name is required'),
  lastName: Yup.string().required('Last Name is required'),
  fianceFirstName: Yup.string().required('Fiance First Name is required'),
  fianceLastName: Yup.string().required('Fiance Last Name is required'),
  weddingDate: Yup.string()
    .trim()
    .matches(/\d{4}-\d{2}-\d{2}/),
  weddingLocation: Yup.string(),
  announcementMessage: Yup.string().max(250),
  announcementShow: Yup.boolean(),
});

interface WeddingDateData {
  updateWeddingWebsite?: Object;
  updateWeddingDate?: Object;
  upsertWeddingDate?: Object;
}

interface BasicInfoData {
  updateMember?: Object | null;
  updateWeddingWebsite?: Object;
}

const updateIFrame = (
  basicInfoData: BasicInfoData,
  weddingDateData: WeddingDateData,
  iframeRef: React.RefObject<HTMLIFrameElement>
) => {
  iframeRef?.current?.contentWindow?.postMessage(
    JSON.stringify({
      type: 'updateBasicInfo',
      data: {
        weddingDatePreferences: weddingDateData.updateWeddingDate || {},
        weddingWebsiteResult: {
          ...(basicInfoData?.updateMember || {}),
          ...(weddingDateData?.updateWeddingWebsite || {}),
          ...(basicInfoData?.updateWeddingWebsite || {}),
        },
        wedding: {
          ...(weddingDateData?.updateWeddingDate || {}),
          ...(weddingDateData?.upsertWeddingDate || {}),
        },
      },
    }),
    getTargetOrigin()
  );
};

interface Props {
  liteSite: boolean;
  className?: string;
  iframeRef?: React.RefObject<HTMLIFrameElement>;
  closeRail?: () => void;
}

export const BasicInfoRail: React.FC<Props> = ({ className, iframeRef, liteSite, closeRail }) => {
  const client = useApolloClient();
  const { pathname } = useLocation();
  const preview = pathname.includes('preview');
  const match = useRouteMatch(basicInfoRailRoute(preview));
  const history = useHistory();
  const { data } = useWeddingWebsiteQuery();
  const [updateBasicInfo] = useUpdateBasicInfoMutation();
  const [createAnnouncement] = useCreateAnnouncementMutation();
  const [updateAnnouncement] = useUpdateAnnouncementMutation();
  const [erroring, setErroring] = useState(false);
  const dateFormRef = useRef(null);

  const isPublished = data?.weddingWebsite?.state === 'published';

  const initialValues: InitialValues = {
    firstName: data?.member?.firstName || '',
    lastName: data?.member?.lastName || '',
    fianceFirstName: data?.member?.fianceFirstName || '',
    fianceLastName: data?.member?.fianceLastName || '',
    weddingLocation: data?.weddingWebsite.weddingLocation || '',
    announcementShow: data?.weddingWebsite?.announcement?.show ?? false,
    announcementMessage: data?.weddingWebsite.announcement?.message || '',
  };

  const onClose = () => {
    history.push(preview ? previewRoute : dashboardRoute);
  };

  const onSubmit = async (values: InitialValues) => {
    const { weddingLocation, announcementMessage, announcementShow } = values;

    const member = {
      firstName: values.firstName,
      lastName: values.lastName,
      fianceFirstName: values.fianceFirstName,
      fianceLastName: values.fianceLastName,
    };

    try {
      if (dateFormRef.current) {
        const current: any = dateFormRef.current;
        if (Object.keys(current.errors).length > 0) {
          const errorMessage = Object.values(current.errors).join(' & ');
          throw new Error(errorMessage);
        }
        current.submitForm();
      }

      // Updates basic info
      await updateBasicInfo({
        variables: {
          weddingWebsiteAttrs: {
            weddingLocation,
          },
          membersArgs: getChangedValues<MemberArgs>(initialValues, member),
        },
      });

      const announcementId = data?.weddingWebsite.announcement?.id;
      let updatedAnnouncementData;
      if (!announcementId) {
        if (announcementShow || announcementMessage.length > 0) {
          await createAnnouncement({
            variables: {
              attributes: {
                message: announcementMessage,
                show: announcementShow,
              },
            },
            update(cache, { data }) {
              const website = cache.readQuery<WeddingWebsiteQuery>({ query: weddingWebsiteQuery })!;

              cache.writeQuery({
                query: weddingWebsiteQuery,
                data: {
                  ...website,
                  weddingWebsite: {
                    ...website.weddingWebsite,
                    announcement: data?.createAnnouncement,
                  },
                },
              });
              updatedAnnouncementData = {
                updateWeddingWebsite: {
                  announcement: data?.createAnnouncement,
                },
              };
            },
          });
        }
      } else {
        await updateAnnouncement({
          variables: {
            attributes: {
              message: announcementMessage,
              show: announcementShow,
            },
          },
        });
        updatedAnnouncementData = {
          updateWeddingWebsite: {
            announcement: { message: announcementMessage, show: announcementShow },
          },
        };
      }

      if (iframeRef) {
        // when basic info is updated slug is changed and iframe will need to be reloaded
        // force wedding website query to refetch data from server
        await client.query<WeddingWebsiteQuery>({
          query: weddingWebsiteQuery,
          fetchPolicy: 'network-only',
        });
        // update iframe with location
        updateIFrame({ updateMember: member, updateWeddingWebsite: { weddingLocation } }, {}, iframeRef);

        // update iframe with announcement
        if (updatedAnnouncementData) {
          updateIFrame({}, updatedAnnouncementData, iframeRef);
        }
      }

      onClose();
    } catch (error) {
      Honeybadger.notify(error, {
        message: 'Error updating basic info',
        context: {
          error,
        },
      });
      setErroring(true);
    }
  };

  const onSubmitDatePreference = async (updated: WeddingDatePreferences) => {
    const { weddingDatePreferences } = client.readQuery<WeddingDatePreferencesQuery>({
      query: weddingDatePreferencesQuery,
    })!;
    client.writeQuery({
      query: weddingDatePreferencesQuery,
      data: { weddingDatePreferences: { ...weddingDatePreferences, ...updated } },
    });

    if (iframeRef) {
      updateIFrame({}, { updateWeddingDate: { ...updated } } || {}, iframeRef);
    }
  };

  return (
    <RightRail open={!!match} className={className || ''} onEscapeKey={onClose} overlay={!preview}>
      <Header
        leftButtonText={closeRail !== undefined ? 'Back' : ''}
        leftButtonOnClick={history.goBack}
        onClose={() => {
          if (closeRail !== undefined) closeRail();
          history.goBack();
        }}
      >
        Basic Info
      </Header>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        data-testid="basic-info-form"
      >
        {({ submitForm, isSubmitting, setFieldValue, isValid }) => (
          <>
            <Content>
              <CVAlert erroring={erroring} onTimeout={() => setErroring(false)} />
              <NameFormSection />
              <WeddingDatePreferenceForm
                ref={dateFormRef}
                onSubmit={onSubmitDatePreference}
                onError={err => {
                  Honeybadger.notify(err, {
                    message: 'Error updating basic info wedding date preference',
                    context: {
                      err,
                    },
                  });
                  setErroring(true);
                }}
              />
              <div className={`${styles.group} ${styles.weddingLocation}`}>
                <Subhead className={styles.subhead}>Wedding Location</Subhead>
                <FormRow>
                  <FormColumn lg>
                    <WeddingLocationField name="weddingLocation" label="Wedding Location" />
                  </FormColumn>
                </FormRow>
              </div>
              {!liteSite && (
                <>
                  <div className={styles.announcementHeader}>
                    <Subhead>Announcement</Subhead>
                    <FormikField
                      name="announcementShow"
                      setFieldValue={setFieldValue}
                      component={ToggleSwitch}
                      enabledText="Display Announcement"
                      disabledText="Display Announcement"
                      data-testid="annoucement-show"
                    />
                  </div>
                  <div className={styles.announcementTextarea}>
                    <FormRow>
                      <FormColumn xs>
                        <FormikField
                          name="announcementMessage"
                          label="To Our Guests"
                          component={Textarea}
                          maxLength={250}
                          data-testid="announcement-message"
                        />
                      </FormColumn>
                    </FormRow>
                  </div>
                </>
              )}
            </Content>
            <Footer>
              <TripleAction
                primaryOnClick={submitForm}
                primaryText={isPublished ? 'Save & publish' : 'Save'}
                primaryDisabled={!isValid}
                secondaryOnClick={onClose}
                secondaryText="Cancel"
                showSpinner={isSubmitting}
              />
            </Footer>
          </>
        )}
      </Formik>
    </RightRail>
  );
};
